<template>
    <section class="flex justify-between items-center">
        <div
            class="
                flex flex-col
                sm:flex-row sm:items-center sm:space-x-2
                xl:space-x-4
            "
        >
            <h2 class="text-lg truncate max-w-120">
                {{ fullName }}
            </h2>
            <nio-input-group v-if="showPhoneDropdown">
                <nio-select
                    list-class-name="max-h-40"
                    :options="availablePhones"
                    :value="selectedContactPhone"
                    @input="updateConversationPhone"
                />
            </nio-input-group>
            <span class="text-gray-600" v-else>{{ formattedPhone }}</span>
        </div>

        <div class="ml-2 space-x-1 flex-shrink-0" v-if="viewingOwnConversations">
            <nio-button
                :href="selectedConversation.contact_phone.contact.partner.url + selectedConversation.contact_phone.contact.related_id"
                :icon-only="true"
                tag="a"
                target="_blank"
                v-tooltip="{ content: $t('chat.infoBar.accessClientCard'), placement: 'bottom-end' }"
                v-if="showAccessClientCard"
            >
                <img
                    class="w-auto h-4"
                    src="/img/activix-icon-dark-mono.png"
                >
            </nio-button>
            <nio-button
                :icon-only="true"
                @click="openContactModal"
                v-tooltip="{ content: $t('chat.infoBar.contactInfos'), placement: 'bottom-end' }"
                v-if="!$configs.inIframe"
            >
                <nio-icon class="text-lg" name="regular/single-neutral-id-card-3" />
            </nio-button>
            <nio-button
                :icon-only="true"
                @click="openPaymentModal"
                v-tooltip="{ content: $t('chat.infoBar.createPayment'), placement: 'bottom-end' }"
                v-if="hasAccessToPayment"
            >
                <nio-icon class="text-lg" name="regular/accounting-invoice-mail" />
            </nio-button>
            <nio-button
                :icon-only="true"
                @click="openAppointmentModal"
                v-tooltip="{ content: $t('chat.infoBar.addAppointment'), placement: 'bottom-end' }"
                v-if="showCreateAppointment"
            >
                <nio-icon class="text-lg" name="regular/calendar-add" />
            </nio-button>
            <nio-button
                :icon-only="true"
                @click="activateConversation(!selectedConversation.active)"
                v-tooltip="{ content: selectedConversation.active ? $t('chat.infoBar.deactivateConversation') : $t('chat.infoBar.activateConversation'), placement: 'bottom-end' }"
                v-if="userInConversation"
            >
                <nio-icon class="text-lg" name="regular/archive" v-if="selectedConversation.active" />
                <nio-icon class="text-lg" name="regular/drawer-download" v-else />
            </nio-button>
        </div>

        <appointment-modal />
        <contact-modal />
    </section>
</template>

<script>
    import {
        mapState,
        mapGetters,
        mapActions,
        mapMutations,
    } from 'vuex';
    import AppointmentModal from '@/components/Modals/AppointmentModal.vue';
    import ContactModal from '@/components/Modals/ContactModal.vue';
    import Contact from '@/entities/Contact.js';

    export default {
        components: {
            AppointmentModal,
            ContactModal,
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapGetters([
                'hasAccessToAppointment',
                'hasAccessToPayment',
            ]),

            ...mapGetters('chat', [
                'selectedConversation',
                'viewingOwnConversations',
            ]),

            showPhoneDropdown() {
                return this.availablePhones && this.availablePhones.length > 1 && this.lastMessageWasUndelivered;
            },

            lastMessageForCurrentPhone() {
                return this.selectedConversation
                    .messages
                    .filter((message) => {
                        return message.contact_phone_id === this.selectedConversation.contact_phone_id;
                    })[0];
            },

            lastMessageWasUndelivered() {
                return !this.lastMessageForCurrentPhone ||
                    (
                        this.lastMessageForCurrentPhone.status === 'undelivered' &&
                        ['30006', '30005'].includes(this.lastMessageForCurrentPhone.error_code)
                    );
            },

            selectedContactPhone() {
                return this.selectedConversation.contact_phone_id;
            },

            availablePhones() {
                return this.selectedConversation.contact_phone.contact.phones?.filter(phone => {
                    return phone.valid && phone.validated;
                }).map(phone => ({
                    value: phone.id,
                    label: this.formatPhone(phone.number, true),
                }));
            },

            userInConversation() {
                return this.selectedConversation.users.some(user => user.id == this.authUser.id);
            },

            formattedPhone() {
                return this.formatPhone(this.selectedConversation.contact_phone.number, true);
            },

            fullName() {
                return this.formatFullName(this.selectedConversation.contact_phone.contact);
            },

            showCreateAppointment() {
                return this.hasAccessToAppointment && !this.$configs.inIframe;
            },

            showAccessClientCard() {
                return this.selectedConversation.contact_phone.contact.partner && this.selectedConversation.contact_phone.contact.related_id;
            },
        },

        methods: {
            ...mapActions('chat', [
                'changeSelectedConversationActivation',
                'changeSelectedConversationContactPhone',
            ]),

            ...mapMutations('contact', [
                'setEditingContact',
            ]),

            updateConversationPhone(phoneId) {
                this.changeSelectedConversationContactPhone(phoneId);
            },

            openPaymentModal() {
                this.$modal.open('payment', {
                    contact: new Contact(this.selectedConversation.contact_phone.contact),
                });
            },

            openAppointmentModal() {
                this.$modal.open('appointment', {
                    contact: new Contact(this.selectedConversation.contact_phone.contact),
                });
            },

            openContactModal() {
                this.setEditingContact(this.selectedConversation.contact_phone.contact);
                this.$modal.open('contact');
            },

            activateConversation(activate) {
                this.changeSelectedConversationActivation(activate);
            },
        },
    };
</script>
